
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { Permission, Role } from '../../../../models/user.models';
import { RoleService } from '../../../../services/role.service';
import { SnackbarService } from '../../../../services/snackbar.service';

export interface RoleEditDialogData {
  role: Role;
}

@Component({
  selector: 'app-role-edit-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatTooltipModule
],
  templateUrl: './role-edit-dialog.component.html',
  styleUrls: ['./role-edit-dialog.component.scss'],
})
export class RoleEditDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<RoleEditDialogComponent>>(MatDialogRef);
  data = inject<RoleEditDialogData>(MAT_DIALOG_DATA);
  private roleService = inject(RoleService);
  private snackbarService = inject(SnackbarService);

  allPermissions: Permission[] = [];
  permissionGroups: { category: string; permissions: Permission[] }[] = [];
  enabledPermissionIds = new Set<number>();
  private lastSavedPermissionIds = new Set<number>();

  isLoading = false;
  isSaving = false;
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadPermissions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPermissions(): void {
    this.isLoading = true;
    this.roleService
      .getAllPermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (permissions) => {
          this.allPermissions = [...permissions].sort(
            (a, b) => a.sortOrder - b.sortOrder,
          );
          this.buildPermissionGroups();
          this.initializeState();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading permissions:', error);
          this.snackbarService.showError('Failed to load permissions');
          this.isLoading = false;
        },
      });
  }

  private buildPermissionGroups(): void {
    const groupMap = new Map<string, Permission[]>();
    for (const p of this.allPermissions) {
      const cat = p.category ?? 'Other';
      if (!groupMap.has(cat)) groupMap.set(cat, []);
      groupMap.get(cat)!.push(p);
    }
    this.permissionGroups = Array.from(groupMap.entries()).map(
      ([category, permissions]) => ({ category, permissions }),
    );
  }

  private initializeState(): void {
    const ids = new Set<number>(
      this.data.role.permissions?.map((p) => p.id) ?? [],
    );
    this.enabledPermissionIds = ids;
    this.lastSavedPermissionIds = new Set(ids);
  }

  isPermissionEnabled(permission: Permission): boolean {
    return this.enabledPermissionIds.has(permission.id);
  }

  onTogglePermission(permission: Permission, enabled: boolean): void {
    if (this.isProtectedRole() || this.isSaving) return;

    if (enabled) {
      this.enabledPermissionIds.add(permission.id);
    } else {
      this.enabledPermissionIds.delete(permission.id);
    }
    this.savePermissions();
  }

  private savePermissions(): void {
    if (this.isSaving || this.isProtectedRole()) return;

    this.isSaving = true;
    const permissions = this.allPermissions.filter((p) =>
      this.enabledPermissionIds.has(p.id),
    );

    const updatedRole: Partial<Role> = {
      ...this.data.role,
      permissions,
    };

    this.roleService.updateRole(this.data.role.id, updatedRole).subscribe({
      next: (savedRole) => {
        Object.assign(this.data.role, savedRole);
        this.lastSavedPermissionIds = new Set(this.enabledPermissionIds);
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Failed to save permissions:', error);
        this.snackbarService.showError('Failed to save permissions');
        this.enabledPermissionIds = new Set(this.lastSavedPermissionIds);
        this.isSaving = false;
      },
    });
  }

  onClose(): void {
    this.dialogRef.close(this.data.role);
  }

  isProtectedRole(): boolean {
    return (
      this.data.role.isProtectedAdmin === true ||
      this.data.role.name === 'Super Admin'
    );
  }
}
